import { useState, useEffect, useMemo, useCallback } from "react"
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"
import type { IrrelevantInputs } from "../types"

interface ProductDraft {
  name: string
  url: string
}

interface SaleDraft {
  name: string
  slug: string
  endDate: string
}

const EMPTY_PRODUCT: ProductDraft = { name: "", url: "" }
const EMPTY_SALE: SaleDraft = { name: "", slug: "", endDate: "" }

interface IrrelevantInputsModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (inputs: IrrelevantInputs) => void
}

export default function IrrelevantInputsModal({
  isOpen,
  onClose,
  onSubmit,
}: IrrelevantInputsModalProps) {
  const [products, setProducts] = useState<ProductDraft[]>([EMPTY_PRODUCT])
  const [sales, setSales] = useState<SaleDraft[]>([])

  // Fresh drafts each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setProducts([EMPTY_PRODUCT])
      setSales([])
    }
  }, [isOpen])

  const inputs = useMemo(
    (): IrrelevantInputs => ({
      deadProducts: products
        .filter(p => p.name.trim())
        .map(p => ({
          name: p.name.trim(),
          ...(p.url.trim() ? { url: p.url.trim() } : {}),
        })),
      deadSales: sales
        .filter(s => s.name.trim() && s.slug.trim())
        .map(s => ({
          name: s.name.trim(),
          slug: s.slug.trim(),
          ...(s.endDate ? { endDate: s.endDate } : {}),
        })),
    }),
    [products, sales],
  )

  const hasEntries =
    inputs.deadProducts.length > 0 || inputs.deadSales.length > 0

  const updateProduct = useCallback(
    (index: number, patch: Partial<ProductDraft>) => {
      setProducts(prev =>
        prev.map((p, i) => (i === index ? { ...p, ...patch } : p)),
      )
    },
    [],
  )

  const updateSale = useCallback((index: number, patch: Partial<SaleDraft>) => {
    setSales(prev => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }, [])

  const handleSubmit = useCallback(() => {
    onSubmit(inputs)
  }, [inputs, onSubmit])

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={620}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      <div className="px-6 py-4 border-b border-basic-blue">
        <h2 className="text-lg font-semibold text-white">
          Archive Irrelevant Ads
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Declare discontinued products and ended sales — ads still pointing at
          them become archive candidates.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">
            Dead products
          </h3>
          <div className="space-y-2">
            {products.map((product, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  placeholder="Product name"
                  value={product.name}
                  fullWidth
                  onChange={e => updateProduct(index, { name: e.target.value })}
                />
                <Input
                  placeholder="Product URL (optional)"
                  value={product.url}
                  fullWidth
                  onChange={e => updateProduct(index, { url: e.target.value })}
                />
                <button
                  type="button"
                  title="Remove product"
                  className="btn btn-ghost btn-xs px-1 text-gray-400 hover:text-white"
                  onClick={() =>
                    setProducts(prev => prev.filter((_, i) => i !== index))
                  }
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button
            variant={{ variant: "text" }}
            size="xs"
            className="mt-2 text-blue-300"
            onClick={() => setProducts(prev => [...prev, EMPTY_PRODUCT])}
          >
            <PlusIcon className="w-4 h-4" />
            Add product
          </Button>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">
            Ended sales
          </h3>
          <div className="space-y-2">
            {sales.map((sale, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  placeholder="Sale name"
                  value={sale.name}
                  fullWidth
                  onChange={e => updateSale(index, { name: e.target.value })}
                />
                <Input
                  placeholder="Slug (e.g. MEMORIAL25)"
                  value={sale.slug}
                  fullWidth
                  onChange={e => updateSale(index, { slug: e.target.value })}
                />
                <Input
                  type="date"
                  title="End date (optional)"
                  value={sale.endDate}
                  onChange={e => updateSale(index, { endDate: e.target.value })}
                />
                <button
                  type="button"
                  title="Remove sale"
                  className="btn btn-ghost btn-xs px-1 text-gray-400 hover:text-white"
                  onClick={() =>
                    setSales(prev => prev.filter((_, i) => i !== index))
                  }
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button
            variant={{ variant: "text" }}
            size="xs"
            className="mt-2 text-blue-300"
            onClick={() => setSales(prev => [...prev, EMPTY_SALE])}
          >
            <PlusIcon className="w-4 h-4" />
            Add sale
          </Button>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-3">
        <Button variant={{ variant: "outlined" }} size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={{ variant: "solid", color: "blue" }}
          size="sm"
          disabled={!hasEntries}
          onClick={handleSubmit}
        >
          Find Irrelevant Ads
        </Button>
      </div>
    </Dialog>
  )
}
